import {Injectable} from '@angular/core';
import {HttpHeaders} from '@angular/common/http';
import {HttpService} from '@core/services';
import {ApiCollection, ApiResponse, Company, CompanyBilling, CompanySubscription, Membership} from '@core/types';
import {ENDPOINTS, GET_URL, log} from '@core/utils';
import {AuthStateService} from '@auth/states';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private membershipsCache: {items: Membership[]; fetchedAt: number} | null = null;
  private membershipsInFlight: Promise<ApiResponse<Membership[], 'memberships'>> | null = null;
  private readonly membershipsTtlMs = 60_000;

  constructor(
    protected apiService: HttpService,
    private authState: AuthStateService
  ) {}

  async memberships(options?: {skipLoader?: boolean; force?: boolean}): Promise<ApiResponse<Membership[], 'memberships'>> {
    const force = !!options?.force;
    const now = Date.now();
    if (!force && this.membershipsCache && now - this.membershipsCache.fetchedAt <= this.membershipsTtlMs) {
      return {success: true, memberships: this.membershipsCache.items};
    }

    if (!force && this.membershipsInFlight) {
      return this.membershipsInFlight;
    }

    this.membershipsInFlight = (async () => {
      try {
        const url = GET_URL(ENDPOINTS.MY_MEMBERSHIPS, {pagination: 0});
        const headers = this.buildHeaders(undefined, options?.skipLoader);
        const response: any = await this.apiService.get(url, headers ? {headers} : undefined);
        const items = this.apiService.normalizeCollection<Membership>(response).member;
        this.membershipsCache = {items, fetchedAt: Date.now()};
        return {success: true, memberships: items};
      } catch (error: any) {
        log.error('Error loading memberships:', error);
      } finally {
        this.membershipsInFlight = null;
      }
      return {
        success: false,
        message: '🚫 Lo sentimos, no pudimos procesar tu solicitud. Por favor intenta nuevamente más tarde.'
      };
    })();

    return this.membershipsInFlight;
  }

  async currentCompanyMemberships(options?: {skipLoader?: boolean}): Promise<ApiResponse<Membership[], 'memberships'>> {
    try {
      const companyId = this.authState.isAdmin() ? undefined : null;
      const url = GET_URL(ENDPOINTS.COMPANY_MEMBERSHIPS, companyId ? {companyId} : {});
      const headers = this.buildHeaders(undefined, options?.skipLoader);
      const response: any = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, memberships: this.apiService.normalizeCollection<Membership>(response).member};
    } catch (error: any) {
      log.error('Error loading current company memberships:', error);
    }
    return {
      success: false,
      message: '🚫 Lo sentimos, no pudimos cargar el equipo de la empresa.'
    }
  }

  async getCompany(companyId: string | number): Promise<ApiResponse<Company>> {
    try {
      const url = GET_URL(ENDPOINTS.COMPANY_DETAIL, {id: companyId});
      const headers = this.buildHeaders(companyId);
      const response: Company = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error loading company:', error);
    }
    return {
      success: false,
      message: '🚫 Lo sentimos, no pudimos cargar la empresa.'
    }
  }

  async getSubscriptions(companyId: string | number): Promise<ApiResponse<CompanySubscription[]>> {
    try {
      const url = GET_URL(ENDPOINTS.COMPANY_SUBSCRIPTIONS, {id: companyId});
      const headers = this.buildHeaders(companyId);
      const response: ApiCollection<CompanySubscription> = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.apiService.normalizeCollection<CompanySubscription>(response).member};
    } catch (error: any) {
      log.error('Error loading subscriptions:', error);
    }
    return {
      success: false,
      message: '🚫 Lo sentimos, no pudimos cargar las suscripciones.'
    }
  }

  async getBilling(companyId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<CompanyBilling>> {
    try {
      const url = GET_URL(ENDPOINTS.COMPANY_BILLING, {id: companyId});
      const headers = this.buildHeaders(companyId, options?.skipLoader);
      const response: CompanyBilling = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error loading billing:', error);
    }
    return {
      success: false,
      message: '🚫 Lo sentimos, no pudimos cargar la facturacion.'
    }
  }

  async uploadLogo(companyId: string | number, file: File, options?: {skipLoader?: boolean}): Promise<ApiResponse<Company>> {
    try {
      const url = GET_URL(ENDPOINTS.COMPANY_LOGO, {id: companyId});
      const formData = new FormData();
      formData.append('file', file, file.name);
      const headers = this.buildHeaders(companyId, options?.skipLoader);
      const response: Company = await this.apiService.postForm(url, formData, headers ? {headers} : undefined);
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error uploading company logo:', error);
    }
    return {
      success: false,
      message: '🚫 Lo sentimos, no pudimos actualizar el logo.'
    }
  }

  private buildHeaders(companyId?: string | number, skipLoader = false): HttpHeaders | null {
    let headers = new HttpHeaders();

    if (skipLoader) {
      headers = headers.set('X-Skip-Loader', '1');
    }

    if (!this.authState.isAdmin() && companyId) {
      headers = headers.set('X-Selected-Company-ID', String(companyId));
    }

    return headers.keys().length ? headers : null;
  }
}
